import { Field, FormikHelpers, FormikProps } from "formik";
import React, { useCallback, useMemo, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import * as Yup from "yup";
import FormProvider from "../../../../components/forms/FormProvider";
import Input from "../../../../components/forms/Input";
import { useMembers } from "../../../../context/members";
import { useWorkspaces } from "../../../../context/workspaces";
import { Workspace } from "../../../../utils/types";

export interface Props extends RouteComponentProps<{}> {}

const GeneralPage: React.FC<Props> = () => {
  const { workspaces, currentWorkspaceId, updateWorkspace } = useWorkspaces();
  const { isCurrentUserAdmin } = useMembers();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const initialValues = useMemo(() => {
    const currentWorkspace =
      !!currentWorkspaceId && workspaces[currentWorkspaceId];

    return currentWorkspace
      ? workspaceToFormValues(currentWorkspace)
      : { name: "" };
  }, [currentWorkspaceId, workspaces]);

  const onSubmit = useCallback(
    async (
      formValues: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      setErrorMessage(null);

      const apiResponse = await updateWorkspace(formValues);

      if (!apiResponse || !apiResponse.status) {
        setErrorMessage("Something went wrong");
      }

      formikHelpers.setSubmitting(false);
    },
    [updateWorkspace]
  );

  return (
    <main className="flex flex-col w-full">
      <div className="mb-6">
        <h1 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
          Workspace settings
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
          Configure your workspace here
        </p>
      </div>

      <FormProvider
        error={errorMessage}
        formikProps={{
          onSubmit,
          initialValues,
          validationSchema: Yup.object().shape({
            name: Yup.string()
              .min(3, "Please enter at least 3 characters")
              .required("Required")
          })
        }}
        classnames={{ form: "dark:bg-transparent" }}
        disabled={!isCurrentUserAdmin}
        orderedFieldNames={ORDERED_FIELD_NAMES}
      >
        {({ isSubmitting }: FormikProps<FormValues>) => {
          return (
            <Field
              id="name"
              name="name"
              label="Name"
              placeholder="My team name"
              type="text"
              classnames={{ wrapper: "w-full sm:w-1/2" }}
              component={Input}
              disabled={isSubmitting || !isCurrentUserAdmin}
            />
          );
        }}
      </FormProvider>
    </main>
  );
};

export default GeneralPage;

//
// Utils
//

interface FormValues {
  name: string;
}

const ORDERED_FIELD_NAMES = ["name"];

const workspaceToFormValues = ({ name }: Workspace): FormValues => ({
  name
});
